/**
 * Create the Accident Start Agent node
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { searchFAQs, formatSearchResults } from '../../utils/faq-search';

export function createAccidentStartAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'accident_start_agent',
    name: 'Accident Start Agent',
    description: 'Step 1: Emergency check, photo upload, image analysis',
    
    // Initial prompt to trigger AI behavior

    
    // Backend provides the actual system instruction via ephemeral token

    
    // Initial prompt to trigger the AI to start with emergency check
    initialPrompt: 'Start accident reporting NOW. In ONE SINGLE TURN: say the 999 emergency warning AND immediately call show_documentation_options AND verbally present both options (upload images / live camera). Do NOT pause between the warning and the options. Do NOT wait for acknowledgment.',
    
    entryGuardrails: requireLanguage(),
    
    exitGuardrails: {
      transitions: new Map([
        ['proceed_to_collect', {
          target: { agentId: 'accident_collect_submit_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['cancel', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'router_agent' },
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'show_documentation_options') {
        console.log('[AccidentStartAgent] Showing documentation options UI');
        
        return {
          toolName,
          args,
          result: {
            success: true,
            message: 'Options UI is now visible. User can verbally select their preferred method.',
          },
        };
      }
      
      if (toolName === 'select_documentation_method') {
        const method = args.method || 'upload_images';
        console.log('[AccidentStartAgent] User selected method:', method);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            method,
            message: method === 'upload_images'
              ? 'Upload UI is now visible. Wait for the vision analysis message before speaking.'
              : 'Call show_camera_arrow now and guide user to open the camera.',
          },
          // For image upload: suppress response.create entirely so the AI says nothing
          // while waiting for vision analysis. The vision result will trigger speech itself.
          silentResponse: method === 'upload_images',
        };
      }
      
      if (toolName === 'show_camera_arrow') {
        console.log('[AccidentStartAgent] Showing camera arrow indicator');
        
        return {
          toolName,
          args,
          result: {
            success: true,
            message: 'Camera arrow is now visible, pointing to camera button.',
          },
        };
      }
      
      if (toolName === 'validate_camera_content') {
        const showsAccident = args.shows_accident || false;
        const contentDescription = args.content_description || '';
        console.log('[AccidentStartAgent] Camera content validation:', { showsAccident, contentDescription });
        
        return {
          toolName,
          args,
          result: {
            success: true,
            shows_accident: showsAccident,
            message: showsAccident 
              ? 'Camera shows accident damage - good!' 
              : 'Camera not showing accident - guide user to redirect',
          },
        };
      }
      
      if (toolName === 'proceed_to_collect') {
        const imageAnalysis = args.image_analysis || '';

        // Guard: refuse transition if vision analysis has not completed yet.
        // This prevents the AI from transitioning before the backend vision model returns,
        // which would cause hallucinated descriptions and the wrong agent receiving the vision context.
        if (context.transitionData.visionAnalysisComplete === false) {
          const lang = context.transitionData.language || 'ar';
          const waitMsg = lang === 'ar'
            ? 'لم يكتمل تحليل الصور بعد. انتظر حتى تصل رسالة "تحليل الحادث جاهز" قبل استدعاء هذه الأداة.'
            : lang === 'ur'
            ? 'تصاویر کا تجزیہ ابھی مکمل نہیں ہوا۔ "وژن تجزیہ تیار ہے" پیغام آنے تک انتظار کریں۔'
            : 'Vision analysis not complete yet. Wait for the "Vision analysis ready" message before calling this tool.';
          console.warn('[AccidentStartAgent] proceed_to_collect blocked — vision not complete yet');
          return {
            toolName,
            args,
            result: { success: false, message: waitMsg },
            triggersTransition: false,
            silentResponse: true,
          };
        }

        // Use AI-reported count, cross-check against transitionData for safety
        const aiReportedCount = args.image_count || 0;
        const actualCount = context.transitionData.uploadedImageCount || 0;
        const finalCount = actualCount > 0 ? actualCount : aiReportedCount;
        if (actualCount > 0 && actualCount !== aiReportedCount) {
          console.warn('[AccidentStartAgent] image_count mismatch — AI:', aiReportedCount, 'system:', actualCount);
        }
        console.log('[AccidentStartAgent] Proceeding to data collection. image_count:', finalCount, 'Analysis:', imageAnalysis);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            message: 'Proceeding to accident data collection. Analysis already announced to user.',
          },
          triggersTransition: true,
          transitionTo: 'accident_collect_submit_agent',
          transitionData: {
            accidentData: {
              imageAnalysis,
              imageCount: finalCount,
            },
          },
        };
      }
      
      if (toolName === 'cancel_accident') {
        console.log('[AccidentStartAgent] User cancelled accident reporting');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Cancelled' },
          triggersTransition: true,
          transitionTo: 'router_agent',
        };
      }
      
      // Inline FAQ - answer questions without leaving flow
      if (toolName === 'search_faq') {
        const question = args.question || '';
        const searchResults = await searchFAQs(question, 3, 2);
        const language = context.transitionData.language || 'en';
        const formattedResults = formatSearchResults(searchResults, language);
        
        console.log('[AccidentStartAgent] Inline FAQ:', question);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            results_count: searchResults.length,
            results: formattedResults,
            message: 'Answer the question, then guide user back to uploading photos.',
          },
          triggersTransition: false,
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      console.log('[AccidentStartAgent] Activated - Step 1 of accident reporting');
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[AccidentStartAgent] Deactivated');
    },
  };
}

export default createAccidentStartAgent;

